// frontend/src/components/MessageItem.js - Using StreamingMessageRenderer
import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    VStack,
    Text,
    Flex,
    useColorModeValue,
    useBreakpointValue,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import MessageMenu from './MessageMenu';
import useMarkdownComponents from './MarkdownComponents';
import QuickMessageActions from './QuickMessageActions';
import StreamingMessageRenderer from './StreamingMessageRenderer';
import { useModelOptions } from './hooks/useOptions';

const MessageItem = React.memo(({
    message,
    messageIndex,
    onDuplicateConversation,
    onScrollToBottom,
    isStreaming,
}) => {
    const isBotMessage = message.sender === 'bot';
    const botBg = useColorModeValue('chat.botBg', 'white');
    const userBg = useColorModeValue('chat.userBg', 'white');
    const [isHovered, setIsHovered] = useState(false);
    const isMobile = useBreakpointValue({ base: true, md: false });
    const showMenu = (isMobile || isHovered) && !isStreaming;
    const markdownComponents = useMarkdownComponents();
    const messageBoxRef = useRef(null);
    const { options: modelOptions } = useModelOptions();

    const senderLabel = useMemo(() => {
        if (!isBotMessage) return 'You';

        if (message.model_display_name && message.system_prompt_display_name) {
            const modelName = message.model_display_name;
            const systemPromptName = message.system_prompt_display_name;

            const deprecated = message.model_slug &&
                !modelOptions.some(option => option.value === message.model_slug);
            const deprecatedSuffix = deprecated ? ' <em>(deprecated)</em>' : '';

            return `${modelName}${deprecatedSuffix} • ${systemPromptName}`;
        }

        return 'Assistant';
    }, [
        isBotMessage,
        message.model_display_name,
        message.system_prompt_display_name,
        message.model_slug,
        modelOptions
    ]);

    const renderedContent = useMemo(() => {
        if (!message || !message.text) {
            return (
                <Text fontSize="sm" color="text.muted" fontStyle="italic">
                    No content to display.
                </Text>
            );
        }

        // Use smart streaming renderer for bot messages that are streaming
        if (isBotMessage && isStreaming) {
            return (
                <StreamingMessageRenderer
                    content={message.text}
                    components={markdownComponents}
                />
            );
        }

        // Standard markdown for completed messages
        return (
            <Box className="markdown-content">
                <ReactMarkdown
                    components={markdownComponents}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    skipHtml={false}
                    linkTarget="_blank"
                >
                    {message.text}
                </ReactMarkdown>
            </Box>
        );
    }, [message.text, isBotMessage, isStreaming, markdownComponents]);

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            ref={messageBoxRef}
            role="group"
            w="full"
            data-message-id={message.id}
        >
            <Box
                w="full"
                display="flex"
                justifyContent={isBotMessage ? 'flex-start' : 'flex-end'}
            >

                <VStack
                    align={isBotMessage ? 'flex-start' : 'flex-end'}
                    spacing={1}
                    // Mobile: use almost full width. Desktop: comfortable reading width.
                    maxW={{
                        base: isBotMessage ? '95%' : '88%',
                        md: isBotMessage ? '85%' : '75%',
                    }}
                    minW="0"
                    position="relative"
                >
                    <Box
                        bg={isBotMessage ? botBg : userBg}
                        p={{ base: 3, md: 4 }}   // less padding on mobile
                        borderRadius="lg"
                        border="1px"
                        borderColor="border.primary"
                        position="relative"
                        w="full"
                        minW="0"
                        overflowWrap="break-word"
                        transition="all 0.2s"
                        _hover={{ boxShadow: 'sm' }}
                    >
                        <Box minW="0" overflowWrap="break-word">
                            {renderedContent}
                        </Box>

                        <Box
                            position="absolute"
                            top={2}
                            right={2}
                            opacity={showMenu ? 1 : 0}
                            transitionProperty="opacity"
                            transitionDuration="0.2s"
                            pointerEvents={showMenu ? 'auto' : 'none'}
                        >
                            <MessageMenu
                                onDuplicate={
                                    !isBotMessage
                                        ? undefined
                                        : () => onDuplicateConversation(messageIndex)
                                }
                                messageContent={message.text || ''}
                                isUserMessage={!isBotMessage}
                            />
                        </Box>

                        {isHovered && !isStreaming && (
                            <QuickMessageActions
                                message={message}
                                messageIndex={messageIndex}
                                onDuplicateConversation={onDuplicateConversation}
                                isBotMessage={isBotMessage}
                                messageBoxRef={messageBoxRef}
                                onScrollToBottom={onScrollToBottom}
                            />
                        )}
                    </Box>

                    <Flex direction="column" align={isBotMessage ? 'flex-start' : 'flex-end'} gap={0}>
                        <Text
                            fontSize="xs"
                            color="text.muted"
                            sx={{
                                '& em': {
                                    fontStyle: 'italic',
                                    color: 'gray.500',
                                    fontSize: 'xs'
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: senderLabel }}
                        />
                        {isStreaming && (
                            <Text
                                fontSize="xs"
                                color="brand.500"
                                fontStyle="italic"
                            >
                                typing...
                            </Text>
                        )}
                    </Flex>
                </VStack>
            </Box>
        </Box>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.message.id === nextProps.message.id &&
        prevProps.message.text === nextProps.message.text &&
        prevProps.isStreaming === nextProps.isStreaming
    );
});

MessageItem.displayName = 'MessageItem';

export default MessageItem;